/* eslint-disable @typescript-eslint/no-restricted-imports */
import isEqual from 'lodash/isEqual';
import { useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';

import {
  useQuestionLibraryActions,
  useQuestionLibraryStore,
} from '~/common/components/QuestionLibrary/store/questionLibraryStore';

import {
  DISBURSEMENT_AMOUNT,
  LAND_ONLY_AMOUNT,
  TAX_PAYMENT_TYPES,
} from '../constants';
import {
  getSupplementalTaxDefaultPaidType,
  getSupplementalTaxPaymentOptions,
} from '../helpers';
import type { TaxesFormValues } from '../taxFormUtils';

type TaxInstallment = NonNullable<TaxesFormValues['installments']>[number];
type AdditionalTax = NonNullable<TaxesFormValues['futureTaxes']>[number];
type SupplementalTax = NonNullable<
  TaxesFormValues['supplementalTaxes']
>[number];
type TaxRow = TaxInstallment | AdditionalTax | SupplementalTax;

const getDelinquentTotal = <T>(
  items: T[] | null | undefined,
  prefix: string,
  getValues: (key: string) => string,
): number =>
  (items ?? []).reduce((acc: number, _, index: number) => {
    const paidType = Number.parseInt(getValues(`${prefix}.${index}.paidType`));
    if (paidType !== TAX_PAYMENT_TYPES.DELINQUENT) return acc;
    return (
      acc +
      (Number.parseFloat(getValues(`${prefix}.${index}.installmentAmount`)) ||
        0)
    );
  }, 0);

const isDistributionAfterDiscountWindow = (
  discountDateStr: string | undefined,
  distributionDateStr: string | undefined,
): boolean => {
  if (!discountDateStr || !distributionDateStr) return false;
  const discountDate = new Date(discountDateStr);
  if (Number.isNaN(discountDate.getTime())) return false;
  const discountDatePlus60Days = new Date(discountDate);
  discountDatePlus60Days.setDate(discountDatePlus60Days.getDate() + 60);
  const distributionDate = new Date(distributionDateStr);
  if (Number.isNaN(distributionDate.getTime())) return false;
  return distributionDate > discountDatePlus60Days;
};

export const useUpdateInstallmentAmountsWhenUnequalToggle = () => {
  const { watch, setValue } = useFormContext();
  const unequalInstallments = watch('unequalInstallments');
  const allValues = watch('installments');

  useEffect(() => {
    if (unequalInstallments === false) {
      const firstInstallmentAmount = allValues[0]?.installmentAmount || 0;
      allValues.map((values: TaxInstallment, index: number) => {
        if (
          index !== 0 &&
          firstInstallmentAmount !== values.installmentAmount
        ) {
          setValue(
            `installments.${index}.installmentAmount`,
            firstInstallmentAmount,
          );
        }
      });
    }
  }, [allValues, setValue, unequalInstallments]);
  return null;
};

export const useUpdateFutureInstallmentsToMatchEscrowInstallmentsIfMonthAndDayAreTheSame =
  () => {
    const { watch, setValue, getValues } = useFormContext();
    const isUnequalInstallments = watch('unequalInstallments');
    const screenData = useQuestionLibraryStore((s) => s.screenData);
    const updateScreenData = useQuestionLibraryActions().updateScreenData;

    useEffect(() => {
      const futureTaxes = screenData?.initialData?.futureTaxes;
      const escrowInstallments = screenData?.initialData?.installments;
      if (!futureTaxes?.length || !escrowInstallments?.length) return;

      escrowInstallments.forEach((escrow: TaxInstallment, index: number) => {
        const escrowDate = new Date(escrow.distributionDate);
        const escrowMonth = escrowDate.getMonth();
        const escrowDay = escrowDate.getDate();

        futureTaxes.forEach((tax: AdditionalTax, taxindex: number) => {
          const taxDate = new Date(tax.distributionDate);
          const taxMonth = taxDate.getMonth();
          const taxDay = taxDate.getDate();
          if (escrowMonth === taxMonth && escrowDay === taxDay) {
            const currentValue = getValues(
              `futureTaxes.${taxindex}.installmentAmount`,
            );
            const newValue = getValues(
              `installments.${index}.installmentAmount`,
            );
            if (
              Number.parseFloat(String(currentValue)) !==
              Number.parseFloat(String(newValue))
            ) {
              setValue(`futureTaxes.${taxindex}.installmentAmount`, newValue);
            }
          }
        });
      });
    }, [
      setValue,
      getValues,
      isUnequalInstallments,
      screenData?.initialData?.installments,
      screenData?.initialData?.futureTaxes,
      updateScreenData,
    ]);

    return null;
  };

export const useUpdateFutureInstallmentsToMatchEscrowInstallmentAmount = () => {
  const { watch, setValue } = useFormContext();
  const isUnequalInstallments = watch('unequalInstallments');
  const futureTaxes = watch('futureTaxes');
  const installments = watch('installments');
  const screenData = useQuestionLibraryStore((s) => s.screenData);
  const futureTaxesInitialData = screenData?.initialData?.futureTaxes;
  const escrowInstallments = screenData?.initialData?.installments;

  const prevInstallmentAmountRef = useRef({
    installments:
      escrowInstallments?.map(
        (escrow: TaxInstallment) => escrow.installmentAmount,
      ) || [],
  });

  useEffect(() => {
    if (!futureTaxesInitialData?.length || !escrowInstallments?.length) return;
    escrowInstallments.forEach((escrow: TaxInstallment, index: number) => {
      const installment = installments[index]?.installmentAmount || 0;
      const escrowDate = new Date(escrow.distributionDate);
      const escrowMonth = escrowDate.getMonth();
      const escrowDay = escrowDate.getDate();

      futureTaxesInitialData.forEach((tax: AdditionalTax, idx: number) => {
        const taxDate = new Date(tax.distributionDate);
        const taxMonth = taxDate.getMonth();
        const taxDay = taxDate.getDate();

        if (escrowMonth === taxMonth && escrowDay === taxDay) {
          const currentValue = futureTaxes[idx]?.installmentAmount || 0;
          if (isUnequalInstallments) {
            if (
              Number.parseFloat(currentValue) !== Number.parseFloat(installment)
            ) {
              setValue(`futureTaxes.${idx}.installmentAmount`, installment);
            }
          } else if (
            Number.parseFloat(
              prevInstallmentAmountRef.current.installments[index],
            ) === Number.parseFloat(currentValue) &&
            Number.parseFloat(currentValue) !== Number.parseFloat(installment)
          ) {
            setValue(`futureTaxes.${idx}.installmentAmount`, installment);
          }
        }
      });
    });
    prevInstallmentAmountRef.current.installments = installments.map(
      (installment: TaxInstallment) => installment.installmentAmount,
    );
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [
    watch,
    setValue,
    isUnequalInstallments,
    futureTaxesInitialData,
    escrowInstallments,
    installments,
  ]);

  return null;
};

export const useResetUnequalInstallmentsWhenMillageRateToggled = () => {
  const { watch, setValue } = useFormContext();
  const initialValues = useQuestionLibraryStore(
    (s) => s.screenData?.initialData,
  );
  const isMillageRate = watch('isMillageRate');

  useEffect(() => {
    if (isMillageRate) {
      setValue('unequalInstallments', false);
    } else {
      setValue('millageRate', 0);
      setValue('taxRate', 0);
    }
    (initialValues?.installments ?? []).forEach((_, index) => {
      setValue(
        `installments.${index}.installmentAmount`,
        initialValues.installments[index].installmentAmount,
      );
    });
  }, [isMillageRate, setValue, initialValues]);

  return null;
};

export const useUpdateDisbursementLabels = () => {
  const screenData = useQuestionLibraryStore((s) => s.screenData);
  const { updateScreenData } = useQuestionLibraryActions();

  useEffect(() => {
    if (screenData?.areTaxesBasedOnLandOnlyValue) {
      return updateScreenData({
        disbursementLabel: LAND_ONLY_AMOUNT,
      });
    }
    updateScreenData({
      disbursementLabel: DISBURSEMENT_AMOUNT,
    });
  }, [screenData?.areTaxesBasedOnLandOnlyValue, updateScreenData]);

  return null;
};

export const useUpdateAnnualAmountLabel = () => {
  const screenData = useQuestionLibraryStore((s) => s.screenData);
  const { updateScreenData } = useQuestionLibraryActions();

  useEffect(() => {
    const annualAmountLabel = screenData?.areTaxesBasedOnLandOnlyValue
      ? 'Land Only Annual Amount'
      : 'Annual Amount';
    updateScreenData({ annualAmountLabel });
  }, [screenData?.areTaxesBasedOnLandOnlyValue, updateScreenData]);
  return null;
};

export const useMillageRateValue = () => {
  const { watch, setValue } = useFormContext();
  const isMillageRate = watch('isMillageRate');
  const millageRate = watch('millageRate');
  const taxRate = watch('taxRate');
  const newConstruction = watch('newConstruction');

  const initialValues = useQuestionLibraryStore(
    (s) => s.screenData?.initialData,
  );
  const totalValue = useQuestionLibraryStore((s) => s.screenData?.totalValue);

  useEffect(() => {
    if (isMillageRate) {
      setValue('millageRate', initialValues?.millageRate);
      setValue('taxRate', initialValues?.taxRate);
    }
  }, [isMillageRate, setValue, initialValues]);

  useEffect(() => {
    if (!isMillageRate && initialValues?.millageRate > 0) {
      setValue('millageRate', 0);
      setValue('taxRate', 0);
    }
  }, [isMillageRate, setValue, initialValues]);

  useEffect(() => {
    if (
      initialValues?.installments.length &&
      isMillageRate &&
      !newConstruction
    ) {
      const rate = taxRate / 100 || millageRate / 1000;
      const total = totalValue * rate;
      const installmentAmount = total / initialValues.installments.length;
      initialValues.installments.forEach((_, index) => {
        setValue(`installments.${index}.installmentAmount`, installmentAmount);
      });
    }
  }, [
    millageRate,
    taxRate,
    setValue,
    isMillageRate,
    initialValues,
    totalValue,
    newConstruction,
  ]);

  return null;
};

export const useDelinquentTaxAmount = () => {
  const { watch } = useFormContext();
  const delinquentTaxAmount = watch('delinquentTaxAmount');
  const { updateScreenData } = useQuestionLibraryActions();
  useEffect(() => {
    updateScreenData({ hasDelinquentTaxes: (delinquentTaxAmount || 0) > 0 });
  }, [delinquentTaxAmount, updateScreenData]);
  return null;
};

export const useManageSupplementalTaxRows = () => {
  const { watch, getValues, reset, setValue, clearErrors } = useFormContext();
  const includeSupplementalTaxes = watch('includeSupplementalTaxes');
  const supplementalTaxes = watch('supplementalTaxes');
  const screenData = useQuestionLibraryStore((s) => s.screenData);
  const { updateScreenData } = useQuestionLibraryActions();
  const wasCheckedRef = useRef<boolean>(includeSupplementalTaxes);

  useEffect(() => {
    const wasChecked = wasCheckedRef.current;
    wasCheckedRef.current = includeSupplementalTaxes;

    const currentSupplementalTaxes = getValues('supplementalTaxes') ?? [];

    if (
      includeSupplementalTaxes &&
      wasChecked === false &&
      currentSupplementalTaxes.length === 0
    ) {
      reset(
        { ...getValues(), supplementalTaxes: [{}] },
        { errors: true, isDirty: true, touched: true, dirtyFields: true },
      );
    }

    if (!includeSupplementalTaxes && wasChecked === true) {
      if (currentSupplementalTaxes.length > 0) {
        reset(
          { ...getValues(), supplementalTaxes: [] },
          { errors: true, isDirty: true, touched: true, dirtyFields: true },
        );
      }
      clearErrors('supplementalTaxes');
    }
  }, [clearErrors, includeSupplementalTaxes, getValues, reset]);

  useEffect(() => {
    if (!includeSupplementalTaxes) return;

    const paymentOptions: Record<string, unknown> = {};

    (supplementalTaxes ?? []).forEach(
      (supplementalTax: SupplementalTax, index: number) => {
        const key = `supplementalTaxes.${index}.paidType`;
        const rowOptions = getSupplementalTaxPaymentOptions({
          supplementalDate: supplementalTax?.distributionDate,
          effectiveClosingDate: screenData?.effectiveClosingDate,
          firstPaymentDate: screenData?.firstPaymentDate,
          isTracLoan: screenData?.isTracLoan,
          isTaxEscrowWaived: screenData?.isTaxEscrowWaived,
        });
        paymentOptions[key] = rowOptions;

        const defaultPaidType = getSupplementalTaxDefaultPaidType({
          supplementalDate: supplementalTax?.distributionDate,
          effectiveClosingDate: screenData?.effectiveClosingDate,
          firstPaymentDate: screenData?.firstPaymentDate,
          isTracLoan: screenData?.isTracLoan,
        });
        const currentPaidType = getValues(key);
        if (
          defaultPaidType !== undefined &&
          (currentPaidType === undefined ||
            currentPaidType === null ||
            currentPaidType === '')
        ) {
          setValue(key, defaultPaidType, {
            shouldDirty: true,
            shouldValidate: true,
          });
        }
      },
    );

    const hasChanges = Object.entries(paymentOptions).some(
      ([key, value]) => !isEqual(screenData?.[key], value),
    );
    if (hasChanges) {
      updateScreenData(paymentOptions);
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [
    getValues,
    includeSupplementalTaxes,
    screenData?.effectiveClosingDate,
    screenData?.firstPaymentDate,
    screenData?.isTaxEscrowWaived,
    screenData?.isTracLoan,
    setValue,
    supplementalTaxes,
    updateScreenData,
  ]);

  return null;
};

export const useDelinquentTaxInputExceedsThresholdAlert = () => {
  const { watch, getValues } = useFormContext();
  const delinquentTaxAmount = watch('delinquentTaxAmount');
  const installments = watch('installments');
  const pastTaxes = watch('pastTaxes');
  const screenData = useQuestionLibraryStore((s) => s.screenData);
  const { updateScreenData } = useQuestionLibraryActions();
  const totalLoanAmount = screenData?.totalLoanAmount ?? 0;
  const cashToClose = screenData?.cashToClose ?? 0;
  const totalNonRealEstateDebt = screenData?.totalNonRealEstateDebt ?? 0;

  useEffect(() => {
    const parsedDelinquentTaxAmount =
      Number.parseFloat(delinquentTaxAmount) || 0;
    if (parsedDelinquentTaxAmount <= 0) {
      if (screenData?.showLPDelinquentInputAlert) {
        updateScreenData({ showLPDelinquentInputAlert: false });
      }
      return;
    }

    const delinquentInstallmentsTotal = getDelinquentTotal(
      installments,
      'installments',
      getValues,
    );
    const delinquentPastTaxesTotal = getDelinquentTotal(
      pastTaxes,
      'pastTaxes',
      getValues,
    );
    const totalDelinquent =
      delinquentInstallmentsTotal +
      delinquentPastTaxesTotal +
      parsedDelinquentTaxAmount;

    const cashToCloseLessCreditCardsAndOtherDebtPaidOff =
      cashToClose - totalNonRealEstateDebt;
    let exceedsThreshold: boolean;
    if (cashToCloseLessCreditCardsAndOtherDebtPaidOff >= totalDelinquent) {
      exceedsThreshold = false;
    } else {
      const threshold = Math.max(2000, totalLoanAmount * 0.01);
      const delinquentTaxesNotCoveredByCashToClose = Math.abs(
        cashToCloseLessCreditCardsAndOtherDebtPaidOff - totalDelinquent,
      );
      exceedsThreshold = delinquentTaxesNotCoveredByCashToClose > threshold;
    }

    if (exceedsThreshold !== screenData?.showLPDelinquentInputAlert) {
      updateScreenData({ showLPDelinquentInputAlert: exceedsThreshold });
    }
  }, [
    delinquentTaxAmount,
    installments,
    pastTaxes,
    screenData?.showLPDelinquentInputAlert,
    updateScreenData,
    totalLoanAmount,
    cashToClose,
    totalNonRealEstateDebt,
    getValues,
  ]);

  return null;
};

export const useDelinquentInputHasValueAlert = () => {
  const { watch, getValues } = useFormContext();
  const delinquentTaxAmount = watch('delinquentTaxAmount');
  const installments = watch('installments');
  const pastTaxes = watch('pastTaxes');
  const screenData = useQuestionLibraryStore((s) => s.screenData);
  const { updateScreenData } = useQuestionLibraryActions();
  const cashToClose = screenData?.cashToClose ?? 0;

  useEffect(() => {
    const delinquentInstallmentsTotal = getDelinquentTotal(
      installments,
      'installments',
      getValues,
    );
    const delinquentPastTaxesTotal = getDelinquentTotal(
      pastTaxes,
      'pastTaxes',
      getValues,
    );
    const totalDelinquent =
      delinquentInstallmentsTotal +
      delinquentPastTaxesTotal +
      (Number.parseFloat(delinquentTaxAmount) || 0);
    const shouldShow = totalDelinquent > 0 && totalDelinquent > cashToClose;

    if (shouldShow !== screenData?.showDUDelinquentInputAlert) {
      updateScreenData({ showDUDelinquentInputAlert: shouldShow });
    }
  }, [
    delinquentTaxAmount,
    installments,
    pastTaxes,
    screenData?.showDUDelinquentInputAlert,
    updateScreenData,
    cashToClose,
    getValues,
  ]);

  return null;
};

export const useDisplayDelinquentAlert = () => {
  const { watch, getValues } = useFormContext();
  const installments = watch('installments');
  const screenData = useQuestionLibraryStore((s) => s.screenData);
  const { updateScreenData } = useQuestionLibraryActions();

  useEffect(() => {
    const escrowInstallments = screenData?.initialData?.installments;
    if (!installments?.length || !escrowInstallments?.length) return;

    const anyDelinquent = installments.some(
      (_: TaxInstallment, index: number) => {
        const paidType = Number.parseInt(
          getValues(`installments.${index}.paidType`),
        );
        if (paidType !== TAX_PAYMENT_TYPES.DELINQUENT) return false;

        return isDistributionAfterDiscountWindow(
          escrowInstallments?.[index]?.discountDate,
          getValues(`installments.${index}.distributionDate`),
        );
      },
    );

    if (anyDelinquent !== screenData?.showDelinquentInstallmentAlert) {
      updateScreenData({ showDelinquentInstallmentAlert: anyDelinquent });
    }
  }, [
    getValues,
    installments,
    screenData?.initialData?.installments,
    screenData?.showDelinquentInstallmentAlert,
    updateScreenData,
  ]);

  return null;
};

export const usePastTaxesDelinquentAlert = () => {
  const { watch, getValues } = useFormContext();
  const pastTaxes = watch('pastTaxes');
  const screenData = useQuestionLibraryStore((s) => s.screenData);
  const { updateScreenData } = useQuestionLibraryActions();

  useEffect(() => {
    const pastTaxesData = screenData?.initialData?.pastTaxes;
    if (!pastTaxes?.length || !pastTaxesData?.length) return;

    const anyDelinquent = pastTaxes.some((_: AdditionalTax, index: number) => {
      const paidType = Number.parseInt(
        getValues(`pastTaxes.${index}.paidType`),
      );
      if (paidType !== TAX_PAYMENT_TYPES.DELINQUENT) return false;

      return isDistributionAfterDiscountWindow(
        pastTaxesData?.[index]?.discountDate,
        getValues(`pastTaxes.${index}.distributionDate`),
      );
    });

    if (anyDelinquent !== screenData?.showPastTaxesDelinquentAlert) {
      updateScreenData({ showPastTaxesDelinquentAlert: anyDelinquent });
    }
  }, [
    getValues,
    pastTaxes,
    screenData?.initialData?.pastTaxes,
    screenData?.showPastTaxesDelinquentAlert,
    updateScreenData,
  ]);

  return null;
};

export const useDelinquentExceedsAcceptableThreshold = () => {
  const { watch, getValues } = useFormContext();
  const installments = watch('installments');
  const screenData = useQuestionLibraryStore((s) => s.screenData);
  const { updateScreenData } = useQuestionLibraryActions();
  const totalLoanAmount = screenData?.totalLoanAmount ?? 0;
  const delinquentTaxAmount = watch('delinquentTaxAmount');

  useEffect(() => {
    const totalDelinquentInstallments = getDelinquentTotal(
      installments,
      'installments',
      getValues,
    );
    const totalPastDelinquentAmount = getDelinquentTotal(
      screenData?.initialData?.pastTaxes,
      'pastTaxes',
      getValues,
    );
    const totalDelinquentAmount =
      totalDelinquentInstallments +
      totalPastDelinquentAmount +
      (Number.parseFloat(delinquentTaxAmount) || 0);
    const exceedsThreshold =
      totalDelinquentAmount > 2000 ||
      totalDelinquentAmount > totalLoanAmount * 0.01;

    if (
      totalDelinquentInstallments > 0 &&
      exceedsThreshold !== screenData?.showDelinquentExceedsThresholdAlert
    ) {
      updateScreenData({
        showDelinquentExceedsThresholdAlert: exceedsThreshold,
      });
    }
    if (
      totalPastDelinquentAmount > 0 &&
      exceedsThreshold !== screenData?.showPastDelinquentExceedsThresholdAlert
    ) {
      updateScreenData({
        showPastDelinquentExceedsThresholdAlert: exceedsThreshold,
      });
    }
  }, [
    getValues,
    installments,
    screenData.initialData.installments,
    screenData?.showDelinquentExceedsThresholdAlert,
    screenData?.showPastDelinquentExceedsThresholdAlert,
    updateScreenData,
    totalLoanAmount,
    delinquentTaxAmount,
    screenData?.initialData?.pastTaxes,
  ]);

  return null;
};

export const useTotalAmount = () => {
  const { watch, setValue } = useFormContext();
  const allInstallments = watch('installments');
  const supplementalTaxes = watch('supplementalTaxes');
  const includeSupplementalTaxes = watch('includeSupplementalTaxes');
  const isMillageRate = watch('isMillageRate');
  const isNonAdValorem = watch('isNonAdValorem');
  const nonAdValorem = watch('nonAdValorem');
  const millageRate = watch('millageRate');
  const taxRate = watch('taxRate');
  const totalValue = useQuestionLibraryStore((s) => s.screenData?.totalValue);
  const { updateScreenData } = useQuestionLibraryActions();

  const totalInstallments = useMemo(() => {
    return allInstallments.reduce((acc: number, curr: TaxInstallment) => {
      return acc + Number.parseFloat(String(curr.installmentAmount || 0));
    }, 0);
  }, [allInstallments]);

  const totalSupplemental = useMemo(() => {
    if (!includeSupplementalTaxes) return 0;
    return (supplementalTaxes ?? []).reduce((acc: number, curr: any) => {
      const paidType = Number.parseInt(String(curr.paidType));
      if (
        paidType === TAX_PAYMENT_TYPES.BORROWER_TO_PAY_AT_CLOSING ||
        paidType === TAX_PAYMENT_TYPES.COLLECT_IN_ESCROW
      ) {
        return acc + Number.parseFloat(String(curr.installmentAmount || 0));
      }
      return acc;
    }, 0);
  }, [supplementalTaxes, includeSupplementalTaxes]);

  useEffect(() => {
    const rate = taxRate / 100 || millageRate / 1000;
    if (isMillageRate) {
      const total = totalValue * rate;
      setValue('totalAmount', total + totalSupplemental);
    } else if (isNonAdValorem) {
      setValue(
        'totalAmount',
        totalInstallments + Number.parseFloat(nonAdValorem) + totalSupplemental,
      );
    } else {
      setValue('totalAmount', totalInstallments + totalSupplemental);
    }
  }, [
    totalSupplemental,
    includeSupplementalTaxes,
    setValue,
    isMillageRate,
    isNonAdValorem,
    nonAdValorem,
    totalInstallments,
    millageRate,
    totalValue,
    taxRate,
  ]);

  useEffect(() => {
    const shouldHideToggle = allInstallments.some(
      ({ installmentAmount }) =>
        Number.parseFloat(installmentAmount || 0) === 0,
    );
    updateScreenData({
      hideExemptToggle: !shouldHideToggle,
    });
  }, [allInstallments, updateScreenData]);

  return null;
};

export const useIsExemptToggle = () => {
  const { watch, setValue } = useFormContext();
  const isExempt = watch('isBorrowerExempt');
  const allInstallments = watch('installments');

  useEffect(() => {
    if (!isExempt) return;
    allInstallments.forEach((_, idx) =>
      setValue(`installments.${idx}.installmentAmount`, 0),
    );
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [isExempt]);

  return null;
};

export const useAdditionalTaxesAlertMessage = () => {
  const { watch, setValue } = useFormContext();
  const additionalTaxes = watch('additionalTaxesPaidAtClosing');
  const installments = watch('installments');
  const pastTaxes = watch('pastTaxes');
  const futureTaxes = watch('futureTaxes');
  const screenData = useQuestionLibraryStore((s) => s.screenData);
  const { updateScreenData } = useQuestionLibraryActions();

  useEffect(() => {
    const payAtClosingTypes = new Set<number>([
      TAX_PAYMENT_TYPES.BORROWER_TO_PAY_AT_CLOSING,
      TAX_PAYMENT_TYPES.SELLER_TO_PAY_AT_CLOSING,
      TAX_PAYMENT_TYPES.PAST_BILL_PAY_AT_CLOSING,
      TAX_PAYMENT_TYPES.CURRENT_BILL_PAY_AT_CLOSING,
    ]);

    const toNumber = (value: unknown): number => {
      if (typeof value === 'number') return value;
      if (typeof value === 'string') {
        return Number.parseFloat(value.replaceAll(',', '')) || 0;
      }
      return 0;
    };

    const additionalAmount = toNumber(additionalTaxes);
    const getPayAtClosingTotal = (items: TaxRow[] | undefined) =>
      (items ?? []).reduce((total: number, installment: TaxRow) => {
        const paidType = Number.parseInt(String(installment?.paidType), 10);
        if (!payAtClosingTypes.has(paidType)) return total;
        return total + toNumber(installment?.installmentAmount);
      }, 0);

    const payAtClosingTotal =
      getPayAtClosingTotal(installments) +
      getPayAtClosingTotal(pastTaxes) +
      getPayAtClosingTotal(futureTaxes);
    const showAdditionalTaxesPaidAtClosing = payAtClosingTotal > 0;

    const threshold = payAtClosingTotal * 1.5;
    const additionalExceedsThreshold =
      payAtClosingTotal > 0 && additionalAmount > threshold;

    if (additionalExceedsThreshold !== screenData?.additionalExceedsThreshold) {
      updateScreenData({ additionalExceedsThreshold });
    }

    if (
      showAdditionalTaxesPaidAtClosing !==
      screenData?.showAdditionalTaxesPaidAtClosing
    ) {
      updateScreenData({ showAdditionalTaxesPaidAtClosing });
    }

    if (!showAdditionalTaxesPaidAtClosing && additionalAmount !== 0) {
      setValue('additionalTaxesPaidAtClosing', 0);
    }
  }, [
    additionalTaxes,
    installments,
    pastTaxes,
    futureTaxes,
    screenData?.additionalExceedsThreshold,
    screenData?.showAdditionalTaxesPaidAtClosing,
    setValue,
    updateScreenData,
  ]);

  return null;
};
